import _ from 'lodash';

// Define interfaces for better type safety
interface ExtractOptions {
  unmaskMemberId?: boolean;
}

interface LLMUsefulData {
  [key: string]: any;
}

/**
 * Utility function to merge CSS classes
 *
 * @param {string} classes - The tier name (e.g., "TIER_1").
 * @return {string []} - A single string with all class names merged
 */
export const classNames = (...classes: string[]): string => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Mask helper: keep first N chars, mask the rest
 * @param {any} value - The value to mask
 * @param {number} keep - Number of characters to keep unmasked (default: 4)
 * @param {string} char - Character to use for masking (default: '•')
 * @return {string|null} Masked string or null if input is null/undefined
 */
function mask(value: any, keep: number = 4, char: string = '•'): string | null {
  if (value == null) return null;
  const s = String(value);
  return s.slice(0, keep) + char.repeat(Math.max(0, s.length - keep));
}

/**
 * Compact a flat object: drop null/undefined and empty arrays
 * @param {any} obj - The object to compact
 * @return {any} Compacted object with null/undefined and empty arrays removed
 */
function compactObject(obj: any): any {
  return _.omitBy(
    obj,
    (v) => v == null || (Array.isArray(v) && v.length === 0)
  );
}

/**
 * Extract LLM-useful info INCLUDING:
 * - Direct identifiers: loginId, employeeId, individualId, organizationId, employerId, subscriberId
 * - PII: individualName, subscriberName, dob
 * - Lists: dependentMDVIdCardData, teamMembers (IDs), erAdmins, locations
 * Excludes logos/images and UI fluff.
 *
 * @param {any} payload - The data payload to extract information from
 * @param {ExtractOptions} options - Options for extraction including unmaskMemberId flag
 * @return {LLMUsefulData} Extracted and formatted data useful for LLM processing
 */
export function extractLLMUsefulData(
  payload: any,
  options: ExtractOptions = {}
): LLMUsefulData {
  const { unmaskMemberId = true } = options;

  const employer = _.get(payload, 'employerData', {});
  const addr = _.get(employer, 'address', {});
  const planYear = _.get(payload, 'currentPlanYear', {});
  const election = _.get(payload, 'electionData', {});
  const enrolledPlans = _.get(payload, 'electionMDVIdCardData', []);

  // Extract detailed enrolled plan information
  const enrolledPlanDetails = _.map(enrolledPlans, (plan: any) => {
    const coverageDetails = _.get(plan, 'coverageDetails', {});
    return compactObject({
      id: _.get(plan, 'id'),
      benefitKind: _.get(plan, 'benefitKind'),
      planId: _.get(plan, 'planId'),
      planName: _.get(plan, 'planName'),
      individualId: _.get(plan, 'individualId'),
      subscriberId: _.get(plan, 'subscriberId'),
      employerId: _.get(plan, 'employerId'),
      individualName: _.get(plan, 'individualName'),
      subscriberName: _.get(plan, 'subscriberName'),
      dob: _.get(plan, 'dob'),
      tier: _.get(plan, 'tier'),
      planYearId: _.get(plan, 'planYearId'),
      // Coverage details with masking option for member ID
      coverageDetails: compactObject({
        effectiveDate: _.get(coverageDetails, 'effectiveDate'),
        endDate: _.get(coverageDetails, 'endDate'),
        memberIdNumber: unmaskMemberId
          ? _.get(coverageDetails, 'memberIdNumber')
          : mask(_.get(coverageDetails, 'memberIdNumber')),
        groupIdNumber: _.get(coverageDetails, 'groupIdNumber'),
        carrierPhone: _.get(coverageDetails, 'carrierPhone'),
        payerName: _.get(coverageDetails, 'payerName'),
        rxBINumber: _.get(coverageDetails, 'rxBINumber'),
        rxPCNumber: _.get(coverageDetails, 'rxPCNumber'),
        rxGroupNumber: _.get(coverageDetails, 'rxGroupNumber'),
        idCardPhoneNumber: _.get(coverageDetails, 'idCardPhoneNumber'),
        alphaPrefix: _.get(coverageDetails, 'alphaPrefix'),
        carrierName: _.get(coverageDetails, 'carrierName'),
        employeeContributionMonthly: _.get(
          coverageDetails,
          'employeeContributionMonthly'
        ),
        employerContributionMonthly: _.get(
          coverageDetails,
          'employerContributionMonthly'
        ),
        userAmended: _.get(coverageDetails, 'userAmended'),
      }),
    });
  });

  // Group enrolled plans by benefit kind for easier access
  const enrolledPlansByKind = _.groupBy(enrolledPlanDetails, 'benefitKind');

  const result = {
    employer: compactObject({
      id: _.get(employer, 'id'),
      name: _.get(employer, 'name'),
      city: _.get(addr, 'city'),
      state: _.get(addr, 'state'),
      zip: _.get(addr, 'zipCode'),
      emailDomains: _.get(employer, 'emailDomains', []),
      groupSize: _.get(employer, 'groupSize'),
      renewalDate: _.get(employer, 'renewalDate'),
    }),

    planYear: compactObject({
      id: _.get(planYear, 'id'),
      name: _.get(planYear, 'name'),
      startDate: _.get(planYear, 'startDate'),
      endDate: _.get(planYear, 'endDate'),
      benefitClasses: _.filter(
        _.map(_.get(planYear, 'benefitClasses', []), (bc) => _.get(bc, 'name'))
      ),
    }),

    // Enrolled plans grouped by benefit kind for easier access
    enrolledPlansByKind,

    // Direct identifiers
    identifiers: compactObject({
      loginId: _.get(payload, 'currentEmployee.loginId'),
      employeeId: _.get(payload, 'currentEmployee.employeeId'),
      individualId: _.get(payload, 'currentEmployee.individualId'),
      organizationId: _.get(payload, 'currentEmployee.organizationId'),
      employerId: _.get(payload, 'currentEmployee.employerId'),
      currentMemberId: _.get(payload, 'currentMemberId'),
      publishedPlansPlanYearId: _.get(
        payload,
        'publishedPlans.currentPlanYearId'
      ),
    }),

    // Include even if empty
    dependentMDVIdCardData: _.get(payload, 'dependentMDVIdCardData', {}),

    // Additional useful context
    planYearInfo: compactObject({
      currentPlanYear: _.get(election, 'currentPlanYear'),
      currentPlanYearId: _.get(election, 'currentPlanYearId'),
    }),
  };

  return result;
}

/**
 * Convenience function to return pretty JSON string of LLM-useful data
 * @param {any} payload - The data payload to extract information from
 * @param {ExtractOptions} options - Options for extraction (e.g., unmaskMemberId)
 * @param {number} space - Number of spaces for JSON formatting indentation
 * @return {string} Pretty-formatted JSON string of extracted data
 */
export function extractLLMUsefulJSON(
  payload: any,
  options: ExtractOptions = {},
  space: number = 2
): string {
  return JSON.stringify(extractLLMUsefulData(payload, options), null, space);
}

/**
 * Filter plans in a benefit section to only include enrolled plans
 * @param {any} section - The benefit section (e.g., medical, dental, vision)
 * @param {any[]} enrolledPlans - Array of enrolled plans for this benefit kind
 * @return {any} Filtered section with only enrolled plans
 */
function filterSectionPlans(section: any, enrolledPlans: any[]): any {
  if (!section || !section.plans || !Array.isArray(section.plans)) {
    return section;
  }

  // Get the planIds from enrolled plans
  const enrolledPlanIds = new Set(
    enrolledPlans.map((plan) => plan.planId).filter(Boolean)
  );

  // If no enrolled plans, return section with empty plans array
  if (enrolledPlanIds.size === 0) {
    return {
      ...section,
      plans: [],
    };
  }

  // Filter plans to only include enrolled ones
  const filteredPlans = section.plans.filter((plan: any) => {
    const planId = plan.planId || plan.id;
    return enrolledPlanIds.has(planId);
  });

  return {
    ...section,
    plans: filteredPlans,
  };
}

/**
 * Filter all benefit sections in benefitGuideContent based on enrolled plans
 * Only filters MDV (Medical, Dental, Vision) plans - other benefit types remain unfiltered
 * @param {any} benefitGuideContent - The full benefit guide content
 * @param {any} enrolledPlansByKind - Enrolled plans grouped by benefit kind (MDV only)
 * @return {any} Filtered benefit guide content with only enrolled MDV plans
 */
function filterBenefitGuidePlans(
  benefitGuideContent: any,
  enrolledPlansByKind: any
): any {
  if (!benefitGuideContent || !enrolledPlansByKind) {
    return benefitGuideContent;
  }

  const filtered = { ...benefitGuideContent };

  // Map of MDV benefit kinds to section names in benefitGuideContent
  // Only filter MDV plans since mdvEnrolledPlans only contains MDV data
  const mdvBenefitKindToSection: Record<string, string> = {
    MEDICAL: 'medical',
    DENTAL: 'dental',
    VISION: 'vision',
  };

  // Filter only MDV benefit sections
  // Other benefit types (life, disability, FSA, HSA, etc.) remain unfiltered
  Object.entries(mdvBenefitKindToSection).forEach(([benefitKind, sectionName]) => {
    if (filtered[sectionName]) {
      const enrolledPlans = enrolledPlansByKind[benefitKind] || [];
      filtered[sectionName] = filterSectionPlans(
        filtered[sectionName],
        enrolledPlans
      );
    }
  });

  return filtered;
}

export const computeNextContext = ({
  idCardData,
  currentContext,
  unmaskMemberId = true,
}: {
  idCardData: any;
  currentContext: any;
  unmaskMemberId?: boolean;
}): any | null => {
  if (!currentContext) return null;

  const hasBG = !!currentContext.benefitGuideContent;

  // Logged in → attach/refresh enrollment details AND filter MDV plans
  if (idCardData?.isLoggedIn && hasBG) {
    const idCardContext = extractLLMUsefulData(idCardData, { unmaskMemberId });

    // Store original unfiltered benefit guide content if not already stored
    // This allows us to restore it when user logs out
    const originalBenefitGuideContent =
      currentContext.benefitGuideContent._originalUnfilteredContent ||
      currentContext.benefitGuideContent;

    // Filter benefit guide MDV plans to only include enrolled plans
    const filteredBenefitGuideContent = filterBenefitGuidePlans(
      originalBenefitGuideContent,
      idCardContext.enrolledPlansByKind
    );

    return {
      ...currentContext,
      benefitGuideContent: {
        ...filteredBenefitGuideContent,
        mdvEnrolledPlans: idCardContext,
        // Store original unfiltered content for restoration on logout
        _originalUnfilteredContent: originalBenefitGuideContent,
      },
    };
  }
  // Logged out → remove enrollment details and restore original unfiltered plans
  if (!idCardData?.isLoggedIn && hasBG) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { mdvEnrolledPlans: _, _originalUnfilteredContent, ...restBG } =
      currentContext.benefitGuideContent || {};

    // If we have the original unfiltered content, restore it
    if (_originalUnfilteredContent) {
      return {
        ...currentContext,
        benefitGuideContent: _originalUnfilteredContent
      };
    }

    // Otherwise, just remove mdvEnrolledPlans
    if (Object.keys(restBG).length > 0) {
      return { ...currentContext, benefitGuideContent: restBG };
    }

    // If benefitGuideContent is now empty, remove it entirely
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { benefitGuideContent: __, ...ctxWithoutBG } = currentContext;
    return ctxWithoutBG;
  }

  // No change needed
  return null;
};
